import { default as xhrStrategy } from './data/strategies/xhr'
import { default as inlineStrategy } from './data/strategies/inline'
import { default as localStrategy } from './data/strategies/local'
import { default as mockStrategy } from './data/strategies/mock'
import { BailOut } from './data/strategies'

export const strategies = {
  XHR: xhrStrategy,
  INLINE: inlineStrategy,
  LOCAL: localStrategy,
  MOCK: mockStrategy
}

const DEFAULT_STRATEGIES = [strategies.XHR]

/**
 * Accepts processing strategies and HTTP request parameters then attempts to process those parameters via the strategies
 * (if the strategy accepts the parameters).
 *
 * Details:
 * Strategies are objects we've defined that deal with HTTP requests in different ways, such as executing them via axios or
 * mocking their return values for testing purposes.
 * When running in production, mock strategies are excluded.
 * All provided strategies that can process a request attempt to process that request.
 * The first successful strategy returns its results until there are no remaining strategies or a strategy generates an
 * error that is not an instance of {@link BailOut}.
 *
 * Convenience Links:
 * {@link https://jsonapi.org/ JSON:API}
 * {@link ./data/strategies/xhr.js}
 * {@link ./data/strategies/inline.js}
 * {@link ./data/strategies/local.js}
 * {@link ./data/strategies/mock.js}
 *
 * @async
 * @function request
 * @param {Object} param0 - An object containing strategies and various other data required to make a request
 * @param {Array} param0.strategies - An array strategy objects
 * @param {Object} param0.params - URL parameters sent with the request
 * @param {string} param0.params.method - The RESTful request method to be used when making the request
 * @param {string} param0.params.url - The server URL that will be used for the request
 * @param {string} param0.params.type - A name representing the data type
 * @param {Object} param0.params.params - The GET or URL parameters
 * @param {Object} param0.params.data - The data being sent in the request
 * @param {Object} param0.params.changes - The updated data being sent in the request if the request is put or patch
 * @param {Object} param0.params.record - The current record data being sent in the request if the request is put or patch
 * @param {Object} param0.params.meta - The HTTP meta elements
 * @returns {Promise<Object>} - The fulfillment of a request based upon the implementation of the strategy, containing data from the
 *                      RESTful request
 */
const request = async ({ strategies, ...params }) => {
  const okToHandle = []

  const extendedStrategies = [...strategies]

  if (FONTAWESOME.ENV !== 'production') {
    extendedStrategies.unshift(mockStrategy)
  }

  for (const strategy of extendedStrategies) {
    const ok = await strategy.accept(params)
    if (ok) okToHandle.push([strategy, ok])
  }

  const process = (handlers, resolve, reject) => {
    if (handlers.length === 0) return reject('No more strategies available')

    const [handler, ...rest] = handlers
    const [strategy, preamble] = handler

    strategy
      .process({ ...params, preamble })
      .then(resolve)
      .catch((...error) => {
        if (error[0] instanceof BailOut) {
          process(rest, resolve, reject)
        } else {
          reject(...error)
        }
      })
  }

  return new Promise((resolve, reject) => {
    process(okToHandle, resolve, reject)
  })
}

/**
 * This method calls {@link request} with the HTTP get method set.
 *
 * @function fetch
 * @param {Object} param0 - An object containing strategies and various other data required to make a request
 * @param {string} param0.url - The server URL that will be used for the request
 * @param {string} param0.type - A name representing the data type
 * @param {Object} param0.params - The GET or URL parameters
 * @param {Array} param0.strategies - An array strategy objects
 * @returns {Promise} - The fulfillment of a request based upon the implementation of the strategy, containing data from the
 *                      RESTful request
 */
export function fetch({ url, type, params, strategies = DEFAULT_STRATEGIES }) {
  return request({ method: 'get', url, type, params, strategies })
}

/**
 * This method calls {@link request} with the HTTP post method set.
 *
 * @function create
 * @param {Object} param0 - An object containing strategies and various other data required to make a request
 * @param {string} param0.url - The server URL that will be used for the request
 * @param {string} param0.type - A name representing the data type
 * @param {Object} param0.data - The data being sent in the request
 * @param {Object} param0.meta - The HTTP meta elements
 * @param {Array} param0.strategies - An array strategy objects
 * @returns {Promise} - The fulfillment of a request based upon the implementation of the strategy, containing data from the
 *                      RESTful request
 */
export function create({ url, type, data, meta, headers, strategies = DEFAULT_STRATEGIES }) {
  return request({ method: 'post', url, type, data, meta, headers, strategies })
}

export function update({ url, patch = false, type, changes, record, meta, strategies = DEFAULT_STRATEGIES }) {
  return request({ method: patch ? 'patch' : 'put', url, type, changes, record, meta, strategies })
}

export function remove({ url, type, meta, strategies = DEFAULT_STRATEGIES }) {
  return request({ method: 'delete', url, type, meta, strategies })
}
